import React from "react";

import { Skeleton } from "antd";
import SkeletonButton from "antd/es/skeleton/Button";
import Highlighter from "react-highlight-words";

import Loading from "../../../../../Common/Loading";
import NoResults from "../../../../../Common/NoResults";
import StyledScrollbar from "../../../../../Common/UIComponents/StyledScrollbar";
import { TEST_SPEC_SUBTITLE_NOT_CONFIGURED_LABEL } from "../Helper";
import TestSpecStatusChip from "./TestSpecStatusChip";

import emptyStateIcon from "../../../../../../assets/images/EmptyState_V2.png";
import listItemArrowIcon from "../../../../../../assets/images/product/specmanager/arrow-right-s-line.svg";
import notConfiguredIcon from "../../../../../../assets/images/product/specmanager/error-warning-fill.svg";
import productCountIcon from "../../../../../../assets/images/product/specmanager/stack-fill (1).svg";

import "./GlobalSpecTable.css";

/**
 * Table for global test spec list.
 * Part of GlobalTestSpec feature - displays test names with Spec Added / Add Specs.
 */
const GlobalSpecTable = ({
  tableData,
  handleEditSpecClick,
  handlePageChangeScroll,
  loadingNewContent,
  specManagerScrollbar,
  loading,
  searchValue,
}) => {
  const handleScrollToBottom = (e) => {
    if (handlePageChangeScroll) {
      const tolerance = 3;
      const bottom = Math.abs(
        Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight),
      ) <= tolerance;
      if (bottom) handlePageChangeScroll();
    }
  };

  return (
    <GlobalSpecTableInner
      tableData={tableData}
      handleEditSpecClick={handleEditSpecClick}
      loadingNewContent={loadingNewContent}
      specManagerScrollbar={specManagerScrollbar}
      loading={loading}
      searchValue={searchValue}
      handleScrollToBottom={handleScrollToBottom}
    />
  );
};

const GlobalSpecTableInner = ({
  tableData,
  handleEditSpecClick,
  loadingNewContent,
  specManagerScrollbar,
  loading,
  searchValue,
  handleScrollToBottom,
}) => {
  if (!loading && tableData.length === 0) {
    return (
      <StyledScrollbar
        id="globalSpecNoResultsScrollbar"
        className="GlobalSpecTable__NoResultsScrollBar"
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
        ref={specManagerScrollbar}
      >
        <NoResults message="No results found" image={emptyStateIcon} />
      </StyledScrollbar>
    );
  }

  return (
    <StyledScrollbar
      disableTrackYWidthCompensation
      disableTracksWidthCompensation
      id="globalSpecDisplayTableScrollBar"
      className="GlobalSpecTable__ScrollBar"
      ref={specManagerScrollbar}
      onScrollStop={handleScrollToBottom}
    >
      <table className="GlobalSpecTable__Table" id="globalSpecTable">
        <tbody>
          {loading && !loadingNewContent && (
            <tr key="skeleton">
              <td>
                <div className="GlobalSpecTable__TableRow GlobalSpecTable__TableRow--skeleton">
                  <Skeleton active paragraph={false} title={{ width: 120 }} />
                  <SkeletonButton active width={120} />
                </div>
              </td>
            </tr>
          )}
          {!(loading && !loadingNewContent) && tableData.map((item, i) => (
            <tr key={`${item.identifier.test}-row-${i}`}>
              <td>
                <div
                  className="GlobalSpecTable__TableRow GlobalSpecTable__TableRow--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    handleEditSpecClick(e, i, item.status);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleEditSpecClick(e, i, item.status);
                    }
                  }}
                >
                  <div className="GlobalSpecTable__TableRowLeftCol">
                    <span className="LinkedFieldsSpan">
                      <Highlighter
                        highlightClassName="GlobalSpecTable__SearchHighlight"
                        searchWords={searchValue ? [searchValue] : []}
                        autoEscape
                        textToHighlight={item.identifier?.test || ""}
                        className="GlobalSpecTable__FieldValue"
                      />
                    </span>
                    {(item.identifier?.subtitle || item.status === "pending") && (
                      <div className="GlobalSpecTable__SubText">
                        {((!item.identifier?.subtitle && item.status === "pending")
                          || item.identifier?.subtitle === TEST_SPEC_SUBTITLE_NOT_CONFIGURED_LABEL) && (
                          <img src={notConfiguredIcon} alt="not configured" className="GlobalSpecTable__SubTextIcon" />
                        )}
                        {item.identifier?.subtitle || TEST_SPEC_SUBTITLE_NOT_CONFIGURED_LABEL}
                      </div>
                    )}
                  </div>
                  <div className="GlobalSpecTable__TableRowRightCol">
                    {item.productCount > 0 && (
                      <div className="GlobalSpecTable__ProductCountBadge">
                        <img src={productCountIcon} alt="products count" className="GlobalSpecTable__ProductCountBadgeIcon" />
                        {item.productCount}
                      </div>
                    )}
                    <TestSpecStatusChip
                      status={item.status}
                      id={item.status === "added" ? "globalSpecSpecEditButton" : "globalSpecSpecAddButton"}
                      addedIconId={item.status === "added" ? "globalSpecSpecAddedIcon" : undefined}
                      pendingIconId={item.status === "pending" ? "globalSpecPendingIcon" : undefined}
                    />
                    <img src={listItemArrowIcon} alt="open spec item" className="GlobalSpecTable__ChevronIcon" />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loadingNewContent && <Loading />}
    </StyledScrollbar>
  );
};

export default GlobalSpecTable;
